/**
 * hasPrintingV2.js — Print Element System
 *
 * Pure element factories on `this.printElements` + `this.printDoc(sections, opts)`.
 * Every factory returns an HTML string. No side effects. No builder chain.
 * Document = array of element strings.
 *
 * Usage:
 *
 *   const { row, col, field, text, bold, table, heading, group,
 *           spacer, divider, line, label, pageBreak, img, list,
 *           grid, watermark, nbsp } = this.printElements;
 *
 *   this.printDoc([
 *     row("between align-end", [
 *       field("SPORTEK D.O.O.", "naziv organizacije", 300),
 *       row("gap-24", [
 *         field("", "mjesto", 140),
 *         field("20.02.2026", "datum", 120),
 *       ]),
 *     ]),
 *     table(headers, items),
 *   ], { pageTitle: "Ulaz robe" });
 *
 * Legacy printDocument({...}) still works as a compat shim.
 */

// ─── Helpers ─────────────────────────────────────────────────────────

function px(v) { return typeof v === "number" ? v + "px" : v; }

/**
 * Parse layout shorthand string into CSS classes + inline styles.
 * Tokens: between, center, end, around, evenly,
 *         align-start, align-end, align-center, wrap,
 *         gap-{n}, mt-{n}, mb-{n}, ml-{n}, mr-{n}, w-{n}
 */
function parseLayout(layout, prefix) {
  if (!layout) return { cls: prefix, style: "" };
  const cls = [prefix];
  const st = [];
  layout.split(/\s+/).forEach((tok) => {
    const numMatch = tok.match(/^(gap|mt|mb|ml|mr|w)-(\d+)$/);
    if (numMatch) {
      const map = { gap: "gap", mt: "margin-top", mb: "margin-bottom", ml: "margin-left", mr: "margin-right", w: "width" };
      st.push(`${map[numMatch[1]]}:${numMatch[2]}px`);
    } else {
      cls.push(`${prefix}--${tok}`);
    }
  });
  return { cls: cls.join(" "), style: st.length ? ` style="${st.join(";")}"` : "" };
}

function join(children) {
  return Array.isArray(children) ? children.filter(Boolean).join("") : (children || "");
}

// ─── Base Styles ─────────────────────────────────────────────────────

const BASE_STYLES = `
  @media print {
    @page { margin: 0; size: auto; }
    body  { margin: 1.5cm; }
    table { border-collapse: collapse; }
    th, td { border: 1px solid #ccc !important; }
    th { background-color: #2d3748 !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    tr:nth-child(even) { background-color: #f5f7fa !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #000; }

  /* Table */
  table { border-collapse: collapse; width: 100%; margin-top: 8px; }
  th, td { border: 1px solid #ccc; text-align: left; padding: 10px 12px; }
  th { background-color: #2d3748; color: #fff; font-weight: 600; font-size: 12px; letter-spacing: 0.03em; }
  td { font-size: 13px; }
  tr:nth-child(even) { background-color: #f5f7fa; }

  /* Row */
  .p-row { display: flex; gap: 16px; }
  .p-row--between      { justify-content: space-between; }
  .p-row--center       { justify-content: center; }
  .p-row--end          { justify-content: flex-end; }
  .p-row--around       { justify-content: space-around; }
  .p-row--evenly       { justify-content: space-evenly; }
  .p-row--align-start  { align-items: flex-start; }
  .p-row--align-end    { align-items: flex-end; }
  .p-row--align-center { align-items: center; }
  .p-row--wrap         { flex-wrap: wrap; }

  /* Column */
  .p-col { display: flex; flex-direction: column; gap: 8px; }
  .p-col--center { align-items: center; }
  .p-col--end    { align-items: flex-end; }
  .p-col--start  { align-items: flex-start; }

  /* Inline group */
  .p-group { display: inline-flex; align-items: flex-end; gap: 6px; }

  /* Underlined field */
  .p-field { display: inline-flex; flex-direction: column; vertical-align: bottom; }
  .p-field__val { border-bottom: 1px solid #000; padding: 2px 6px; min-height: 22px; font-size: 15px; font-weight: 600; text-align: center; }
  .p-field__val--normal { font-weight: normal; }
  .p-field__lbl { font-size: 10px; color: #555; margin-top: 2px; text-align: center; }

  /* Divider */
  .p-divider { border: none; border-top: 1px solid #aaa; margin: 16px 0; width: 100%; }

  /* Signature line */
  .p-line { border-bottom: 1px solid #000; min-width: 180px; height: 1px; }

  /* Box */
  .p-box { border: 1px solid #ccc; padding: 12px; border-radius: 4px; }

  /* Grid */
  .p-grid { display: grid; gap: 16px; }

  /* Page break */
  .p-page-break { page-break-after: always; break-after: page; height: 0; }

  /* Watermark */
  .p-watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 80px; font-weight: bold; color: rgba(0,0,0,0.06); pointer-events: none; z-index: 9999;
    text-transform: uppercase; letter-spacing: 12px; white-space: nowrap; }
`;

// ─── Internal HTML Builders ──────────────────────────────────────────

function buildTable(headers, items, opts = {}) {
  const printable = headers.filter((h) => h.print !== false);
  const hasGroups = printable.some((h) => h.group);

  let thead = "";
  if (hasGroups) {
    const top = [], bottom = [];
    let i = 0;
    while (i < printable.length) {
      const h = printable[i];
      if (h.group) {
        let span = 1;
        while (i + span < printable.length && printable[i + span].group === h.group) span++;
        top.push({ text: h.group, colspan: span, align: "center" });
        for (let j = i; j < i + span; j++) bottom.push(printable[j]);
        i += span;
      } else {
        top.push({ text: h.printText || h.text, rowspan: 2, align: h.align || "left" });
        i++;
      }
    }
    thead = `<thead>
      <tr>${top.map((c) => `<th style="text-align:${c.align}" ${c.colspan ? `colspan="${c.colspan}"` : `rowspan="${c.rowspan}"`}>${c.text}</th>`).join("")}</tr>
      <tr>${bottom.map((h) => `<th style="text-align:${h.align || "left"}">${h.printText || h.text}</th>`).join("")}</tr>
    </thead>`;
  } else {
    thead = `<thead><tr>${printable.map((h) =>
      `<th style="text-align:${h.align || "left"}">${h.printText || h.text}</th>`
    ).join("")}</tr></thead>`;
  }

  const rows = items.map((item) =>
    `<tr>${printable.map((h) => {
      const val = h.value.includes(".")
        ? h.value.split(".").reduce((o, k) => o?.[k] ?? "", item)
        : item[h.value];
      const align = h.align || "left";
      if (h.isImage && val)
        return `<td style="text-align:${align}"><img src="${val}" style="width:60px;height:60px;object-fit:cover"/></td>`;
      return `<td style="text-align:${align}">${val != null ? val : ""}</td>`;
    }).join("")}</tr>`
  ).join("");

  const emptyCount = opts.emptyRows || 0;
  const emptyHtml = emptyCount > 0
    ? Array(emptyCount).fill(`<tr>${printable.map((h) => `<td style="text-align:${h.align || "left"}">&nbsp;</td>`).join("")}</tr>`).join("")
    : "";

  const tbody = `<tbody>${rows}${emptyHtml}</tbody>`;

  const titleHtml = opts.title
    ? `<div style="text-align:center;margin-top:20px;margin-bottom:10px"><h3 style="margin:0">${opts.title}</h3></div>`
    : "";

  return `${titleHtml}<div class="print-table"><table>${thead}${tbody}</table></div>`;
}

function openPrintWindow(bodyHtml, opts = {}) {
  const {
    pageTitle = "Izvještaj",
    styles: extraStyles = "",
    waitForImage = false,
    features = "left=0,top=0,width=3950,height=900,toolbar=0,scrollbars=0,status=0",
  } = opts;

  const win = window.open("", "", features);
  win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${pageTitle}</title>
  <style>${BASE_STYLES}${extraStyles}</style>
</head>
<body onafterprint="window.close()">${bodyHtml}</body>
</html>`);
  win.document.close();

  if (waitForImage) {
    const iv = setInterval(() => {
      const img = win.document.getElementById("printImage");
      if (img?.complete) { clearInterval(iv); setTimeout(() => { win.focus(); win.print(); }, 100); }
    }, 100);
  } else {
    win.focus();
    win.print();
  }
}

// ─── Element Factories ───────────────────────────────────────────────
// All return HTML strings. No side effects. Stateless.

const elements = {
  /**
   * Flex row.
   * row("between align-end mt-14", [child1, child2])
   * row([child1, child2])  — shorthand, no layout
   */
  row(layout, children) {
    if (Array.isArray(layout)) { children = layout; layout = ""; }
    const { cls, style } = parseLayout(layout, "p-row");
    return `<div class="${cls}"${style}>${join(children)}</div>`;
  },

  /**
   * Flex column.
   * col("center gap-12", [child1, child2])
   */
  col(layout, children) {
    if (Array.isArray(layout)) { children = layout; layout = ""; }
    const { cls, style } = parseLayout(layout, "p-col");
    return `<div class="${cls}"${style}>${join(children)}</div>`;
  },

  /**
   * CSS grid.
   * grid(3, [child1, child2, child3])       — 3 equal columns
   * grid("200px 1fr 1fr", [c1, c2, c3])     — custom template
   */
  grid(cols, children) {
    const template = typeof cols === "number" ? `repeat(${cols}, 1fr)` : cols;
    return `<div class="p-grid" style="grid-template-columns:${template}">${join(children)}</div>`;
  },

  /**
   * Underlined field: value above a line, label in parens below.
   * field("SPORTEK", "naziv organizacije", 300)
   * field("", "mjesto", 140)      — blank field for hand-fill
   * field("42", "", 100)          — value only, no label
   * field("42", "broj", { width: 120, bold: false })
   */
  field(value, label, widthOrOpts) {
    let width = 120, isBold = true;
    if (typeof widthOrOpts === "number" || typeof widthOrOpts === "string") {
      width = widthOrOpts;
    } else if (widthOrOpts && typeof widthOrOpts === "object") {
      width = widthOrOpts.width || 120;
      if (widthOrOpts.bold === false) isBold = false;
    }
    const valCls = "p-field__val" + (isBold ? "" : " p-field__val--normal");
    return (
      `<span class="p-field" style="min-width:${px(width)}">` +
      `<span class="${valCls}">${value || "&nbsp;"}</span>` +
      (label ? `<span class="p-field__lbl">(${label})</span>` : "") +
      `</span>`
    );
  },

  /**
   * Text element.
   * text("hello")            — plain span
   * text("hello", 18)        — with font size
   * text("hello", { size: 18, color: "#666", block: true })
   */
  text(content, sizeOrOpts) {
    if (!sizeOrOpts) return `<span>${content}</span>`;
    if (typeof sizeOrOpts === "number") return `<span style="font-size:${sizeOrOpts}px">${content}</span>`;
    const o = sizeOrOpts;
    const st = [];
    if (o.size) st.push(`font-size:${px(o.size)}`);
    if (o.color) st.push(`color:${o.color}`);
    if (o.bold) st.push("font-weight:bold");
    if (o.italic) st.push("font-style:italic");
    if (o.mt != null) st.push(`margin-top:${px(o.mt)}`);
    const tag = o.block ? "div" : "span";
    const style = st.length ? ` style="${st.join(";")}"` : "";
    return `<${tag}${style}>${content}</${tag}>`;
  },

  /**
   * Bold text.
   * bold("Naslov")       — simple
   * bold("Naslov", 18)   — with font size
   */
  bold(content, size) {
    const style = size ? ` style="font-size:${px(size)}"` : "";
    return `<strong${style}>${content}</strong>`;
  },

  /** Italic text. */
  italic(content) { return `<em>${content}</em>`; },

  /**
   * Heading (h2/h3/h4).
   * heading("Naslov")          — h2, centered
   * heading("Naslov", 3)       — h3, centered
   * heading("Naslov", { level: 3, center: false, mt: 20 })
   */
  heading(content, levelOrOpts) {
    let level = 2, center = true, mt, mb;
    if (typeof levelOrOpts === "number") {
      level = levelOrOpts;
    } else if (levelOrOpts) {
      level = levelOrOpts.level || 2;
      center = levelOrOpts.center !== false;
      mt = levelOrOpts.mt;
      mb = levelOrOpts.mb;
    }
    const st = [];
    if (center) st.push("text-align:center");
    if (mt != null) st.push(`margin-top:${px(mt)}`);
    if (mb != null) st.push(`margin-bottom:${px(mb)}`);
    const style = st.length ? ` style="${st.join(";")}"` : "";
    return `<h${level}${style}>${content}</h${level}>`;
  },

  /**
   * Inline group — wraps elements in an inline-flex container.
   * group(bold("Br."), field("42", "", 100))
   * group(text("Od koga"), nbsp(2), field("SPORTEK", "dobavljač", 280))
   */
  group(...els) {
    const arr = els.length === 1 && Array.isArray(els[0]) ? els[0] : els;
    return `<span class="p-group">${arr.filter(Boolean).join("")}</span>`;
  },

  /**
   * Label: Value pair.
   * label("Datum:", "23.02.2026")
   * label("Datum:", "23.02.", { bold: false, size: 14 })
   */
  label(lbl, value, opts = {}) {
    const { bold: isBold = true, size, separator = "" } = opts;
    const st = size ? ` style="font-size:${px(size)}"` : "";
    const l = isBold ? `<strong>${lbl}${separator}</strong>` : `${lbl}${separator}`;
    return `<span${st}>${l} ${value || ""}</span>`;
  },

  /**
   * Data table.
   * table(headers, items)
   * table(headers, items, { title: "Stavke", emptyRows: 5 })
   */
  table(headers, items, opts) {
    if (typeof opts === "string") opts = { title: opts };
    return buildTable(headers, items, opts || {});
  },

  /**
   * Signature line for handwriting.
   * line()      — 180px wide line
   * line(250)   — 250px wide line
   */
  line(width = 180) {
    return `<div class="p-line" style="min-width:${px(width)}"></div>`;
  },

  /** Vertical spacer. spacer() = 16px, spacer(40) = 40px */
  spacer(height = 16) {
    return `<div style="height:${px(height)}"></div>`;
  },

  /**
   * Horizontal divider.
   * divider()           — solid
   * divider("dashed")   — dashed
   */
  divider(style) {
    const s = style ? ` style="border-top-style:${style}"` : "";
    return `<hr class="p-divider"${s}/>`;
  },

  /** Page break — content after this goes to the next printed page. */
  pageBreak() {
    return `<div class="p-page-break"></div>`;
  },

  /**
   * Image element.
   * img("https://...", 120)
   * img("https://...", 120, 80)
   * img("https://...", { width: 120, height: 80, id: "printImage" })
   */
  img(src, widthOrOpts, height) {
    let w, h, id;
    if (typeof widthOrOpts === "object") {
      w = widthOrOpts.width; h = widthOrOpts.height; id = widthOrOpts.id;
    } else {
      w = widthOrOpts; h = height;
    }
    const st = [];
    if (w) st.push(`width:${px(w)}`);
    if (h) st.push(`height:${px(h)}`);
    st.push("object-fit:contain");
    const idAttr = id ? ` id="${id}"` : "";
    return `<img${idAttr} src="${src}" style="${st.join(";")}"/>`;
  },

  /**
   * List (ul or ol).
   * list(["Meso", "Povrće"])           — unordered
   * list(["Meso", "Povrće"], true)     — ordered
   */
  list(items, ordered = false) {
    const tag = ordered ? "ol" : "ul";
    const lis = items.map((item) => `<li>${item}</li>`).join("");
    return `<${tag} style="margin:8px 0;padding-left:24px">${lis}</${tag}>`;
  },

  /**
   * Non-breaking spaces.
   * nbsp()    — 1 space
   * nbsp(4)   — 4 spaces
   */
  nbsp(count = 1) {
    return "&nbsp;".repeat(count);
  },

  /**
   * Watermark overlay text (e.g. "KOPIJA", "ORIGINAL", "DRAFT").
   * watermark("KOPIJA")
   */
  watermark(text) {
    return `<div class="p-watermark">${text}</div>`;
  },

  /**
   * Bordered box container.
   * box([child1, child2])
   * box("mt-12 mb-8", [child1, child2])
   */
  box(layoutOrChildren, children) {
    if (Array.isArray(layoutOrChildren)) { children = layoutOrChildren; layoutOrChildren = ""; }
    const { cls, style } = parseLayout(layoutOrChildren, "p-box");
    return `<div class="${cls}"${style}>${join(children)}</div>`;
  },
};

// ─── Specialized Printers (internal HTML builders) ───────────────────

function buildEmployeeListHtml(employees, companyName, t) {
  return employees.map((emp, i) => {
    const username = emp.email || (emp.card_code ? `${emp.card_code}@sportek` : "N/A");
    return `
      <div style="border-top:1px solid #aaa;${i === employees.length - 1 ? "border-bottom:1px solid #aaa;" : ""}padding:12px 0;display:flex;justify-content:space-between;align-items:flex-start;">
        <div style="width:18%;font-size:1.1em">${emp.name || ""}</div>
        <div style="width:18%;font-size:1.1em">${emp.lastname || ""}</div>
        <div style="font-size:0.9em;width:60%">
          <div style="display:flex;width:100%">
            <div style="width:35%;text-align:right"><strong>${t("username")}:</strong></div>
            <div style="width:65%;padding-left:4px">${username}</div>
          </div>
        </div>
        <div style="font-weight:bold;font-size:0.9em;white-space:nowrap;margin-left:20px;text-transform:uppercase">${companyName || ""}</div>
      </div>`;
  }).join("");
}

function buildEmployeeCardHtml(emp, { isHalfPage = false, orientation = "portrait", mainTitle, subTitle, t }) {
  const lastName = (emp.lastname || "").toUpperCase();
  const firstName = (emp.name || "").toUpperCase();
  let fullName = `${lastName} ${firstName}`.trim();
  if (emp.parent_name) fullName = `${lastName} (${emp.parent_name.toUpperCase()}) ${firstName}`.trim();

  const username = emp.email || (emp.card_code ? `${emp.card_code}@sportek` : "N/A");
  const password = emp.password || emp.id || emp.code || emp.external_code || "N/A";
  const show = (v) => v ? "" : "display:none;";

  const s = isHalfPage ? {
    pad: "12px 16px", maxW: "340px", titleSz: "13px", companySz: "11px",
    nameSz: "12px", textSz: "11px", smallSz: "10px",
    mS: "4px", mM: "6px", imgSz: "16px", qrMg: "6px 0",
  } : {
    pad: "30px", maxW: "500px", titleSz: "20px", companySz: "18px",
    nameSz: "18px", textSz: "16px", smallSz: "14px",
    mS: "8px", mM: "12px", imgSz: "24px", qrMg: "16px 0",
  };
  const w = isHalfPage ? "95%" : (orientation === "landscape" ? "60%" : "80%");

  return `
    <div class="employee-card-wrapper" style="width:${w};max-width:${s.maxW};display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;padding:${s.pad};border:1px solid #ddd;border-radius:8px;background:#fff;box-sizing:border-box;box-shadow:0 2px 4px rgba(0,0,0,0.08)">
      <div style="margin-bottom:${s.mS};font-size:${s.titleSz};font-weight:bold;color:#333">${subTitle ? subTitle : mainTitle ? mainTitle.toUpperCase() : ""}</div>
      <div style="${show(emp.company)}font-size:${s.companySz};margin-bottom:${s.mS};color:#666">${emp.company || ""}</div>
      <div style="font-size:${s.companySz};font-weight:bold;margin-bottom:${s.mS};color:#444">${subTitle ? mainTitle : ""}</div>
      <div style="margin:${s.mM} 0 ${s.mS} 0;font-size:${s.nameSz};font-weight:600;color:#222">${fullName}</div>
      <div style="margin-bottom:${s.mS};font-size:${s.textSz};color:#333"><strong>${t("username")}:</strong> <span style="color:#0066cc">${username}</span></div>
      <div style="${show(emp.password)}margin-bottom:${s.mS};font-size:${s.textSz};color:#333"><strong>${t("password")}:</strong> <span style="color:#0066cc">${password}</span></div>
      <div style="${show(emp.id)}margin-bottom:${s.mS};font-size:${s.textSz};color:#333"><strong>Ordera ID:</strong> <span style="color:#0066cc">${emp.id || ""}</span></div>
      <div style="${show(emp.externalCode)}margin-bottom:${s.mM};font-size:${s.textSz};color:#333"><strong>${t("access_card_id")}:</strong> <span style="color:#0066cc">${emp.externalCode || ""}</span></div>
      <div class="qr-code-container" style="margin:${emp.qr ? s.qrMg : "10px 0 0 0"};font-size:${s.smallSz};color:#666">${emp.qr || ""}</div>
      <div style="font-size:${s.smallSz};color:#666;margin-bottom:${s.mS}">${t("download_app")}: <span style="color:#0066cc">https://ordera.app/mobile</span></div>
      <div style="display:flex;justify-content:center;align-items:center">
        <img id="printImage" src="https://restorani.bitlab.host/img/o.png" alt="ordera.app" style="width:${s.imgSz};height:${s.imgSz};object-fit:contain"/>
      </div>
    </div>`;
}

function buildAllCardsHtml(employees, count, orientation, mainTitle, subTitle, t) {
  const opts = (half) => ({ isHalfPage: half, orientation, mainTitle, subTitle, t });

  if (count === 2) {
    return employees.reduce((html, emp, i) => {
      if (i % 2 !== 0) return html;
      const emp2 = employees[i + 1];
      const isLast = i + 2 >= employees.length;
      return html + `
        <div class="card-page two-cards" style="width:100%;height:100vh;display:flex;flex-direction:column;justify-content:center;align-items:center;gap:20px;box-sizing:border-box;padding:10px 0;${!isLast ? "page-break-after:always" : ""}">
          <div class="card-slot" style="display:flex;justify-content:center;align-items:center;width:100%">${buildEmployeeCardHtml(emp, opts(true))}</div>
          <div style="width:80%;border-top:1px dashed #ccc"></div>
          ${emp2 ? `<div class="card-slot" style="display:flex;justify-content:center;align-items:center;width:100%">${buildEmployeeCardHtml(emp2, opts(true))}</div>` : ""}
        </div>`;
    }, "");
  }

  return employees.map((emp, i) => `
    <div class="card-page" style="width:100%;height:100vh;display:flex;justify-content:center;align-items:center;box-sizing:border-box;${i < employees.length - 1 ? "page-break-after:always" : ""}">
      ${buildEmployeeCardHtml(emp, opts(false))}
    </div>`).join("");
}

function buildMealCardHtml(food, { conf, $store, $t }) {
  const imageUrl = food.image_id
    ? `${conf.apiUrlBase}/gallery/${$store.state.restaurant.id}/medium/${food.image?.name || food.image_id}`
    : "https://via.placeholder.com/260x170?text=No+Image";
  const price = food.price ? Number(food.price).toFixed(2) : "";
  const qrVal = JSON.stringify({ id: food.id, secure: food.qr_secret || "" });
  const qrImg = `<img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(qrVal)}" width="120" height="120"/>`;
  const groceries = (food.groceries || []).map((g) =>
    `<tr><td style="padding:4px 12px">${g.name || ""}</td><td style="padding:4px 12px;text-align:right">${g.pivot?.quantity || ""}</td><td style="padding:4px 12px">${g.measure || ""}</td></tr>`
  ).join("");
  const sideDishes = (food.prilog || []).length
    ? (food.prilog || []).map((p) => `<li style="margin-bottom:2px">&#10003; ${p.name}</li>`).join("")
    : `<li style="color:#444">Nema unesenih priloga</li>`;
  const menuTypes = (food.menu_types || []).map((mt) => $t(mt.group)).join(", ");

  return `
    <div class="meal-card-container" style="width:100%;min-height:100vh;display:flex;justify-content:center;align-items:flex-start;padding:20px;box-sizing:border-box">
      <div class="meal-card" style="width:480px;max-width:90%;padding:32px 24px 24px;font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;background:#fff;border-radius:16px;box-shadow:0 4px 6px rgba(0,0,0,0.1);border:1px solid #ddd">
        <div style="width:100%;padding:18px 18px 10px;background-color:#fb6d3b;color:#fff;border-radius:8px 8px 0 0">
          <div style="font-size:1.6em;font-weight:700;line-height:1.1">${food.name || ""}</div>
          <div style="font-size:1.1em;opacity:.95;margin-top:2px">${$t(food.category?.name || "")}</div>
        </div>
        <div style="display:flex;align-items:center;flex-wrap:wrap">
          <img src="${imageUrl}" style="width:260px;height:170px;object-fit:cover;border-radius:8px;flex-shrink:0"/>
          <div style="flex:1;padding-left:24px;display:flex;flex-direction:column;min-width:200px">
            <div style="font-size:1.08em;margin-bottom:6px"><b>ID</b> ${food.id || ""}</div>
            <div style="font-size:1.08em;margin-bottom:6px"><b>${$t("code")}</b> ${food.code || ""}</div>
            <div style="font-size:1.08em;margin-bottom:6px"><b>${$t("price")}</b> ${price ? price + " " + this.restaurantCurrency : this.restaurantCurrency}</div>
          </div>
        </div>
        <div style="margin-top:18px;font-size:1.08em"><b>${$t("description")}</b> ${food.description || ""}</div>
        <div style="margin-top:4px;font-size:1.08em"><b>${$t("note")}</b> ${food.note || ""}</div>
        <div style="margin-top:22px;font-size:1.18em;font-weight:600">${$t("composition")}</div>
        <table style="width:100%;border-collapse:collapse;font-size:1.05em;margin-top:6px">
          <thead><tr style="background:#fafafa">
            <th style="text-align:left;padding:6px 12px">${$t("grocery")}</th>
            <th style="text-align:right;padding:6px 12px">${$t("quantity")}</th>
            <th style="text-align:left;padding:6px 12px">${$t("unit_of_measurment")}</th>
          </tr></thead>
          <tbody>${groceries}</tbody>
        </table>
        <div style="display:flex;align-items:flex-start;justify-content:space-between;width:100%;margin-top:22px;flex-wrap:wrap">
          <div style="flex:1;min-width:250px">
            <div style="margin-top:22px;font-size:1.08em"><b>${$t("side_dishes")}</b></div>
            <ul style="margin:8px 0 0;padding:0 0 0 18px;font-size:1.08em;list-style:none">${sideDishes}</ul>
            <div style="margin-top:12px;font-size:1.08em"><b>${$t("daily_offer")}:</b><br/>${menuTypes}</div>
          </div>
          <div style="margin-left:16px;flex-shrink:0;display:flex;flex-direction:column;align-items:center">
            ${qrImg}
            <div style="font-size:1em;color:#444;text-align:center;margin-top:4px">${$t("scan_for_details")}</div>
          </div>
        </div>
      </div>
    </div>`;
}

// ─── The Mixin ───────────────────────────────────────────────────────

export default {
  computed: {
    /**
     * Element factories object. Destructure in component methods:
     *   const { row, col, field, text, bold, table, ... } = this.printElements;
     */
    printElements() { return elements; },

    isPrinting() {
      return this.$store.state.isPrinting;
    },
  },

  methods: {
    setPrinter() {
      this.$store.commit("SET_PRINTING");
    },

    /**
     * Print a document from an array of element strings.
     *
     *   this.printDoc([
     *     row("between", [ field("A", "lbl", 200), text("B") ]),
     *     table(headers, items),
     *   ], { pageTitle: "Izvještaj", styles: "", waitForImage: false });
     */
    printDoc(sections, opts = {}) {
      const bodyHtml = sections.filter(Boolean).join("");
      openPrintWindow(bodyHtml, opts);
    },

    // ── Specialized Printers ──

    printEmployees({ title, employees }) {
      this.printDoc([
        buildEmployeeListHtml(employees, title, this.$t.bind(this)),
      ], {
        pageTitle: this.$t("employee_data"),
        styles: "body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;font-size:10pt;margin:1cm}@media print{@page{margin:1cm}body{margin:1cm}}",
      });
    },

    printEmployeeCards({ count = 1, orientation = "portrait", employees, mainTitle, subTitle }) {
      this.printDoc([
        buildAllCardsHtml(employees, count, orientation, mainTitle, subTitle, this.$t.bind(this)),
      ], {
        pageTitle: this.$t("employee_cards"),
        waitForImage: true,
        styles: `
          body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;font-size:10pt;margin:0;padding:0}
          .card-page.two-cards .qr-code-container svg{width:80px!important;height:80px!important}
          @media print{
            @page{margin:${count === 2 ? "5mm" : "15mm"};size:${orientation}}
            body{margin:0;padding:0}
            .card-page{page-break-after:always!important;break-after:always!important;page-break-inside:avoid!important;break-inside:avoid!important;display:flex!important;justify-content:center!important;align-items:center!important;width:100%!important;height:100vh!important;box-sizing:border-box!important;overflow:hidden!important}
            .card-page.two-cards{flex-direction:column!important;gap:15px!important;padding:5px 0!important}
            .card-page.two-cards .qr-code-container svg{width:70px!important;height:70px!important}
            .card-page.two-cards .employee-card-wrapper{max-width:320px!important;padding:10px 14px!important}
            .card-page:last-child{page-break-after:auto!important;break-after:auto!important}
            .employee-card-wrapper{page-break-inside:avoid!important;break-inside:avoid!important;display:flex!important;flex-direction:column!important;justify-content:center!important;align-items:center!important;overflow:hidden!important}
            .card-page:not(.two-cards) .employee-card-wrapper{max-width:500px!important;width:${orientation === "landscape" ? "60%" : "80%"}!important}
            .card-page.two-cards .employee-card-wrapper{max-width:400px!important;width:90%!important}
            *{box-sizing:border-box!important}img{max-width:100%!important;height:auto!important}
            p,div,span,strong{page-break-inside:avoid!important;break-inside:avoid!important;orphans:3;widows:3}
          }
          @media screen{
            .card-page{border:1px dashed #ccc;margin-bottom:20px;background:#fafafa}
            .employee-card-wrapper{box-shadow:0 4px 8px rgba(0,0,0,0.15)}
          }`,
      });
    },

    printMealCard(food) {
      this.printDoc([
        buildMealCardHtml(food, this),
      ], {
        pageTitle: food.name,
        styles: `
          @media print{
            @page{margin:10mm;size:A4 portrait}
            body{background:#f5f5f5!important;margin:0;padding:0}
            .meal-card-container,.meal-card{page-break-inside:avoid!important;break-inside:avoid!important}
            .meal-card-container{display:flex!important;min-height:100vh;width:100%}
            .meal-card{display:block!important;overflow:hidden;max-width:100%}
            img{max-width:100%!important;height:auto!important}
            table{page-break-inside:auto!important;width:100%!important}
            tr{page-break-inside:avoid!important;break-inside:avoid!important}
          }`,
      });
    },

    /**
     * Legacy compat shim — old printDocument({...}) calls work unchanged.
     */
    printDocument(config = {}) {
      const {
        tables = [], pageTitle, title, subtitle,
        additionalStyles, headerHtml, beforeTableHtml, beforeTitleHtml,
        afterTableHtml, hideDatetime, footerHtml,
        dateTimeLocale, dateTimeOptions,
        company1 = {}, company2 = {},
        signature = {},
        printWindowFeatures, hasLogo,
      } = config;

      const { row, col, text, line } = elements;
      const sections = [];

      if (headerHtml) sections.push(headerHtml);

      if (!hideDatetime) {
        const locale = dateTimeLocale || "sr-RS";
        const dtOpts = dateTimeOptions || {
          year: "numeric", month: "2-digit", day: "2-digit",
          hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
        };
        const fmt = new Date().toLocaleString(locale, dtOpts);
        sections.push(`<div style="margin-top:16px;font-size:14px;">${this.$t("report_creation_datetime")}: ${fmt}</div>`);
      }

      if (company1.id) {
        const t = this.$t.bind(this);
        const one = (c) => `
          <div>
            <h2 style="margin-bottom:0">${c.printTitle}</h2>
            <div style="margin-top:16px;font-size:14px;">
              <strong>${t("company_name")}: </strong>${c.name}<br>
              <strong>${t("address")}: </strong>${c.address}<br>
              <strong>${t("phone")}: </strong>${c.phone}<br>
              <strong>${t("email")}: </strong>${c.email}
            </div>
          </div>`;
        sections.push(`<div style="display:flex;justify-content:space-between">${one(company1)}${company2?.id ? one(company2) : ""}</div>`);
      }

      if (beforeTitleHtml) sections.push(beforeTitleHtml);
      if (title || subtitle) {
        if (title) sections.push(`<div style="text-align:center"><h2>${title}</h2></div>`);
        if (subtitle) sections.push(`<div style="text-align:center"><h3>${subtitle}</h3></div>`);
      }
      if (beforeTableHtml) sections.push(beforeTableHtml);

      tables.forEach((t) => sections.push(buildTable(t.headers, t.items, { title: t.title })));

      if (afterTableHtml) sections.push(afterTableHtml);

      const { left = {}, right = {} } = signature;
      if (left.title || left.name || right.title || right.name) {
        sections.push(
          row("between mt-40", [
            col("center gap-4", [text(left.title || ""), line(), text(left.name || "")]),
            col("center gap-4", [text(right.title || ""), line(), text(right.name || "")]),
          ])
        );
      }

      if (footerHtml) sections.push(footerHtml);

      this.printDoc(sections, {
        pageTitle: pageTitle || "Izvještaj",
        styles: additionalStyles || "",
        waitForImage: hasLogo || false,
        features: printWindowFeatures,
      });
    },
  },
};